import React, { useEffect, useRef, useState } from 'react';

/**
 * BuildLanding — single-shot Build landing page.
 *  - Typewriter-animated prompt bubble (centered, large).
 *  - Marketplace grid below with live mini iframe previews of templates.
 *  - Submitting or clicking a template triggers onSubmit(prompt) which
 *    creates a chat and fires Aria's first single-shot code generation.
 */

const TYPEWRITER_PROMPTS = [
  'Build a Notion-style kanban board with drag & drop...',
  'Landing page for an AI fitness coach with pricing tiers...',
  'B2B SaaS dashboard with charts, KPIs and a sidebar...',
  'Portfolio site for a Parisian product designer...',
  'A restaurant landing with reservation form and gallery...',
  'Analytics dashboard for an e-commerce shop...',
];

const TEMPLATES = [
  {
    id: 'saas-landing',
    title: 'SaaS Landing',
    category: 'Marketing',
    desc: 'Hero, features grid, pricing, social proof.',
    prompt:
      'Generate a modern SaaS landing page for a productivity app called "Flow". '
      + 'Dark theme, gradient hero, animated feature cards, 3-tier pricing, '
      + 'testimonials carousel, FAQ accordion and a final CTA. Use Tailwind, '
      + 'Font Awesome icons and Google Fonts (Inter + Space Grotesk).',
    preview: saasPreview(),
  },
  {
    id: 'ai-coach',
    title: 'AI Coach Dashboard',
    category: 'App',
    desc: 'Sidebar nav, daily plan cards, chat panel.',
    prompt:
      'Build an AI fitness coach dashboard (Mode B, Vite + React). Left sidebar '
      + 'with sections (Today, Plan, Progress, Chat). Main panel shows the daily '
      + 'workout as cards with sets/reps, a circular progress widget, and a chat '
      + 'with the coach using framer-motion message bubbles. Dark, modern, '
      + 'turquoise accent.',
    preview: coachPreview(),
  },
  {
    id: 'portfolio',
    title: 'Creative Portfolio',
    category: 'Portfolio',
    desc: 'Editorial typography, project grid.',
    prompt:
      'Generate a creative portfolio for a product designer. Editorial typography '
      + '(Playfair Display + Inter), oversized hero, project grid with hover zoom, '
      + 'about section with timeline, contact CTA. Off-white background, charcoal '
      + 'text, single accent color.',
    preview: portfolioPreview(),
  },
  {
    id: 'ecommerce',
    title: 'E-commerce Boutique',
    category: 'Shop',
    desc: 'Product grid, cart drawer, lookbook.',
    prompt:
      'Generate a minimalist e-commerce boutique landing for a coffee brand. '
      + 'Hero with bag image, story section, product grid (6 items with price), '
      + 'lookbook collage, subscription form, footer with shipping info. '
      + 'Cream background, dark brown accents, serif headings.',
    preview: shopPreview(),
  },
  {
    id: 'analytics',
    title: 'Analytics Dashboard',
    category: 'App',
    desc: 'KPIs, charts, recent activity feed.',
    prompt:
      'Build an analytics dashboard (Mode B, Vite + React) using recharts. Top '
      + 'KPI cards (revenue, orders, conversion, AOV), line chart for revenue '
      + 'over time, bar chart by channel, donut for traffic sources, recent '
      + 'orders table, sidebar nav. Dark UI, electric blue accent.',
    preview: analyticsPreview(),
  },
  {
    id: 'restaurant',
    title: 'Restaurant',
    category: 'Marketing',
    desc: 'Menu, reservation, gallery.',
    prompt:
      'Generate a landing page for a Parisian neo-bistrot called "Maison Lune". '
      + 'Full-bleed hero photo, story section, menu preview by course, gallery '
      + 'grid, reservation form, hours & address. Warm cream palette, deep '
      + 'burgundy accent, Cormorant Garamond + Inter.',
    preview: restaurantPreview(),
  },
  {
    id: 'kanban',
    title: 'Kanban Workspace',
    category: 'App',
    desc: 'Drag & drop columns, task cards.',
    prompt:
      'Build a Kanban workspace (Mode B, Vite + React). 4 columns (Backlog, '
      + 'Doing, Review, Done), draggable cards with priority dots, assignee '
      + 'avatars, due date, label chips. Header with project switcher, search, '
      + 'filters. Clean light UI, soft shadows, lucide-react icons.',
    preview: kanbanPreview(),
  },
  {
    id: 'blog',
    title: 'Editorial Magazine',
    category: 'Content',
    desc: 'Featured article, grid, newsletter.',
    prompt:
      'Generate an editorial magazine homepage. Oversized featured article '
      + 'with image, article grid below (6 cards with category tag + date + '
      + 'author), newsletter inline signup, category navigation, author spotlight. '
      + 'White background, black text, single bold accent color.',
    preview: blogPreview(),
  },
];

export default function BuildLanding({ onSubmit, recentChats = [], onSelectChat }) {
  const [value, setValue] = useState('');
  const [placeholder, setPlaceholder] = useState('');
  const [promptIdx, setPromptIdx] = useState(0);
  const [hasFocus, setHasFocus] = useState(false);
  const textareaRef = useRef(null);

  // Typewriter loop — letters write, hold, erase, next.
  useEffect(() => {
    if (hasFocus || value.length > 0) return undefined;
    const current = TYPEWRITER_PROMPTS[promptIdx];
    let i = 0;
    let phase = 'writing';
    let timer;

    const tick = () => {
      if (phase === 'writing') {
        i += 1;
        setPlaceholder(current.slice(0, i));
        if (i >= current.length) {
          phase = 'holding';
          timer = setTimeout(tick, 1600);
          return;
        }
        timer = setTimeout(tick, 38 + Math.random() * 32);
      } else if (phase === 'holding') {
        phase = 'erasing';
        timer = setTimeout(tick, 30);
      } else if (phase === 'erasing') {
        i -= 1;
        setPlaceholder(current.slice(0, Math.max(0, i)));
        if (i <= 0) {
          setPromptIdx((p) => (p + 1) % TYPEWRITER_PROMPTS.length);
          return;
        }
        timer = setTimeout(tick, 18);
      }
    };
    timer = setTimeout(tick, 60);
    return () => clearTimeout(timer);
  }, [promptIdx, hasFocus, value]);

  const submit = (text) => {
    const v = (text ?? value).trim();
    if (!v) return;
    onSubmit?.(v);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div
      data-testid="build-landing"
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        background:
          'radial-gradient(1200px 600px at 50% -10%, rgba(124,58,237,0.10) 0%, transparent 60%), '
          + 'radial-gradient(900px 600px at 80% 80%, rgba(120,80,255,0.06) 0%, transparent 65%), '
          + 'linear-gradient(180deg, #FBFAF7 0%, #F4F3F0 100%)',
        overflowY: 'auto',
        position: 'relative',
      }}
    >
      {/* Subtle grid backdrop */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          backgroundImage:
            'linear-gradient(rgba(15,15,30,0.035) 1px, transparent 1px),'
            + 'linear-gradient(90deg, rgba(15,15,30,0.035) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse at 50% 0%, black 35%, transparent 80%)',
        }}
      />

      <div
        style={{
          position: 'relative',
          zIndex: 1,
          maxWidth: 1180,
          width: '100%',
          margin: '0 auto',
          padding: '72px 28px 56px',
        }}
      >
        {/* Brand pill */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            padding: '6px 14px',
            background: 'rgba(124,58,237,0.10)',
            border: '1px solid rgba(124,58,237,0.30)',
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 700,
            color: '#048264',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: '#7C3AED',
              boxShadow: '0 0 10px rgba(124,58,237,0.6)',
              animation: 'pulse-dot 1.4s ease-in-out infinite',
            }}
          />
          Aria · single-shot build
        </div>

        <h1
          style={{
            margin: '22px 0 14px',
            fontSize: 'clamp(40px, 5vw, 64px)',
            fontWeight: 800,
            color: '#0F0F1E',
            letterSpacing: '-0.025em',
            lineHeight: 1.04,
            textAlign: 'left',
          }}
        >
          Tell us what to build.
          <br />
          <span
            style={{
              background:
                'linear-gradient(120deg, #00A887 0%, #7C3AED 35%, #6E5BFF 95%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Aria ships it in seconds.
          </span>
        </h1>

        <p
          style={{
            margin: '0 0 36px',
            color: 'rgba(15,15,30,0.6)',
            fontSize: 16,
            lineHeight: 1.6,
            maxWidth: 720,
          }}
        >
          Aria reads your business context — personas, dataroom, current ventures —
          and emits production-ready code on the spot. Skip the planning.
          See the product immediately.
        </p>

        {/* Prompt bubble */}
        <div
          style={{
            position: 'relative',
            background: '#FFFFFF',
            border: hasFocus
              ? '1px solid rgba(124,58,237,0.55)'
              : '1px solid rgba(15,15,30,0.08)',
            borderRadius: 22,
            padding: '4px',
            boxShadow: hasFocus
              ? '0 24px 60px rgba(124,58,237,0.18), 0 0 0 4px rgba(124,58,237,0.08), 0 1px 2px rgba(15,15,30,0.04)'
              : '0 24px 60px rgba(15,15,30,0.07), 0 1px 2px rgba(15,15,30,0.04)',
            transition: 'all 0.25s ease',
          }}
        >
          <textarea
            ref={textareaRef}
            data-testid="build-landing-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onFocus={() => setHasFocus(true)}
            onBlur={() => setHasFocus(false)}
            onKeyDown={onKeyDown}
            placeholder={placeholder || ' '}
            rows={3}
            style={{
              width: '100%',
              minHeight: 84,
              padding: '20px 22px 56px',
              background: 'transparent',
              border: 'none',
              outline: 'none',
              resize: 'none',
              color: '#0F0F1E',
              fontSize: 16,
              lineHeight: 1.55,
              fontFamily: 'inherit',
            }}
          />
          <div
            style={{
              position: 'absolute',
              bottom: 14,
              left: 22,
              right: 14,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <div style={{ display: 'flex', gap: 8, color: 'rgba(15,15,30,0.5)', fontSize: 11 }}>
              <span><kbd style={kbd}>Enter</kbd> to ship</span>
              <span style={{ opacity: 0.5 }}>·</span>
              <span><kbd style={kbd}>Shift</kbd>+<kbd style={kbd}>Enter</kbd> newline</span>
            </div>
            <button
              type="button"
              data-testid="build-landing-submit"
              disabled={!value.trim()}
              onClick={() => submit()}
              style={{
                padding: '10px 18px',
                borderRadius: 12,
                border: 'none',
                background: value.trim()
                  ? 'linear-gradient(135deg, #7C3AED 0%, #5EEAD4 100%)'
                  : 'rgba(15,15,30,0.05)',
                color: value.trim() ? '#04221C' : 'rgba(15,15,30,0.35)',
                fontWeight: 700,
                fontSize: 13,
                letterSpacing: '0.04em',
                cursor: value.trim() ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s',
                boxShadow: value.trim() ? '0 8px 24px rgba(124,58,237,0.32)' : 'none',
              }}
            >
              Ship it →
            </button>
          </div>
        </div>

        {/* Marketplace */}
        <div style={{ marginTop: 64 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              marginBottom: 22,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#048264',
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  marginBottom: 8,
                }}
              >
                Marketplace · curated by Aria
              </div>
              <h2
                style={{
                  margin: 0,
                  fontSize: 24,
                  fontWeight: 700,
                  color: '#0F0F1E',
                  letterSpacing: '-0.01em',
                }}
              >
                Start from a live template.
              </h2>
            </div>
            <span style={{ color: 'rgba(15,15,30,0.5)', fontSize: 13 }}>
              {TEMPLATES.length} templates · 1 click to build
            </span>
          </div>

          <div
            data-testid="build-marketplace-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 18,
            }}
          >
            {TEMPLATES.map((tpl) => (
              <TemplateCard key={tpl.id} tpl={tpl} onPick={() => submit(tpl.prompt)} />
            ))}
          </div>
        </div>

        {/* Recent chats */}
        {recentChats.length > 0 && (
          <div style={{ marginTop: 56 }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: 'rgba(15,15,30,0.5)',
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                marginBottom: 14,
              }}
            >
              Recent builds
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {recentChats.slice(0, 8).map((c) => (
                <button
                  key={c.chat_id}
                  type="button"
                  data-testid={`build-landing-recent-${c.chat_id}`}
                  onClick={() => onSelectChat?.(c.chat_id)}
                  style={{
                    padding: '8px 14px',
                    background: '#FFFFFF',
                    border: '1px solid rgba(15,15,30,0.08)',
                    borderRadius: 999,
                    color: 'rgba(15,15,30,0.75)',
                    fontSize: 12,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    maxWidth: 260,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    boxShadow: '0 1px 2px rgba(15,15,30,0.03)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(124,58,237,0.45)';
                    e.currentTarget.style.color = '#0F0F1E';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(124,58,237,0.10)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(15,15,30,0.08)';
                    e.currentTarget.style.color = 'rgba(15,15,30,0.75)';
                    e.currentTarget.style.boxShadow = '0 1px 2px rgba(15,15,30,0.03)';
                  }}
                >
                  {c.title || 'Untitled'}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse-dot { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes float-up {
          from { transform: translateY(8px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        [data-testid="build-landing"] textarea::placeholder {
          color: rgba(15,15,30,0.38);
          font-style: normal;
        }
      `}</style>
    </div>
  );
}

const kbd = {
  padding: '2px 6px',
  background: 'rgba(15,15,30,0.05)',
  border: '1px solid rgba(15,15,30,0.10)',
  borderRadius: 4,
  fontSize: 10,
  fontFamily: "'JetBrains Mono', monospace",
  color: 'rgba(15,15,30,0.7)',
};

/* ============================================================================ */
/* Template card with live mini iframe preview                                  */
/* ============================================================================ */

function TemplateCard({ tpl, onPick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      data-testid={`build-template-${tpl.id}`}
      onClick={onPick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        textAlign: 'left',
        padding: 0,
        background: '#FFFFFF',
        border: hover
          ? '1px solid rgba(124,58,237,0.45)'
          : '1px solid rgba(15,15,30,0.08)',
        borderRadius: 16,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.25s ease',
        transform: hover ? 'translateY(-3px)' : 'translateY(0)',
        boxShadow: hover
          ? '0 20px 50px rgba(124,58,237,0.16), 0 0 0 1px rgba(124,58,237,0.06)'
          : '0 1px 2px rgba(15,15,30,0.04), 0 8px 24px rgba(15,15,30,0.06)',
        fontFamily: 'inherit',
        color: 'inherit',
      }}
    >
      {/* Live mini preview */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '16 / 10',
          background: '#F7F6F2',
          overflow: 'hidden',
          borderBottom: '1px solid rgba(15,15,30,0.06)',
        }}
      >
        <iframe
          title={tpl.title}
          srcDoc={tpl.preview}
          sandbox=""
          loading="lazy"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '320%',
            height: '320%',
            border: 'none',
            transform: 'scale(0.3125)',
            transformOrigin: '0 0',
            pointerEvents: 'none',
            background: '#fff',
          }}
        />
        {/* hover overlay */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(15,15,30,0.55) 100%)',
            opacity: hover ? 1 : 0,
            transition: 'opacity 0.25s',
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: 10,
            left: 10,
            padding: '3px 8px',
            background: 'rgba(15,15,30,0.78)',
            border: '1px solid rgba(255,255,255,0.18)',
            borderRadius: 999,
            fontSize: 9,
            fontWeight: 700,
            color: '#fff',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            backdropFilter: 'blur(8px)',
          }}
        >
          {tpl.category}
        </div>
        <div
          style={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            padding: '6px 12px',
            background: hover ? '#7C3AED' : 'rgba(15,15,30,0.85)',
            color: hover ? '#04221C' : '#fff',
            fontSize: 11,
            fontWeight: 700,
            borderRadius: 999,
            transition: 'all 0.2s',
            letterSpacing: '0.04em',
            boxShadow: hover ? '0 6px 18px rgba(124,58,237,0.35)' : 'none',
          }}
        >
          Build →
        </div>
      </div>

      <div style={{ padding: '14px 16px 16px' }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: '#0F0F1E',
            marginBottom: 4,
            letterSpacing: '-0.01em',
          }}
        >
          {tpl.title}
        </div>
        <div style={{ fontSize: 12, color: 'rgba(15,15,30,0.55)', lineHeight: 1.45 }}>
          {tpl.desc}
        </div>
      </div>
    </button>
  );
}

/* ============================================================================ */
/* Mini preview HTML — kept dense, designed to look great at 0.3x scale.        */
/* ============================================================================ */

function shell(body, opts = {}) {
  const { bg = '#fff', color = '#111', font = 'Inter, system-ui, sans-serif' } = opts;
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=1200">
<script src="https://cdn.tailwindcss.com"></script>
<style>html,body{margin:0;padding:0;background:${bg};color:${color};font-family:${font}}</style>
</head><body>${body}</body></html>`;
}

function saasPreview() {
  const body = `
  <div class="min-h-screen bg-[#0A0A0F] text-white">
    <nav class="flex items-center justify-between px-12 py-5 border-b border-white/5">
      <div class="flex items-center gap-2"><div class="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-400 to-cyan-400"></div><span class="font-bold">Flow</span></div>
      <div class="flex gap-6 text-sm text-white/60"><span>Product</span><span>Pricing</span><span>Docs</span><span>Login</span></div>
      <button class="px-4 py-2 rounded-lg bg-emerald-400 text-emerald-950 font-bold text-sm">Start free</button>
    </nav>
    <section class="px-12 pt-20 pb-24 text-center max-w-5xl mx-auto">
      <div class="inline-block px-3 py-1 rounded-full border border-white/10 text-xs text-white/70 mb-6">New · AI assist v2</div>
      <h1 class="text-6xl font-bold tracking-tight">Ship product faster.<br/><span class="bg-gradient-to-r from-emerald-400 via-cyan-400 to-indigo-400 bg-clip-text text-transparent">Without the meetings.</span></h1>
      <p class="mt-6 text-white/60 text-lg">Flow turns your team's chaos into a single source of truth. Plan, ship and review — in one place.</p>
      <div class="mt-8 flex justify-center gap-3"><button class="px-6 py-3 rounded-lg bg-emerald-400 text-emerald-950 font-bold">Start free</button><button class="px-6 py-3 rounded-lg border border-white/15 text-white/85">Watch demo</button></div>
    </section>
    <section class="px-12 grid grid-cols-3 gap-5 max-w-6xl mx-auto pb-20">
      ${['Realtime','AI-native','Built for teams'].map((t,i)=>`<div class="p-6 rounded-2xl border border-white/8 bg-white/[0.03]"><div class="w-9 h-9 rounded-lg bg-emerald-400/15 mb-3"></div><div class="font-semibold">${t}</div><div class="text-sm text-white/55 mt-2">Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod.</div></div>`).join('')}
    </section>
    <section class="px-12 grid grid-cols-3 gap-5 max-w-6xl mx-auto pb-24">
      ${[['Starter','$0','For solo'],['Pro','$24','For teams'],['Scale','$96','Unlimited']].map((p,i)=>`<div class="p-6 rounded-2xl border ${i===1?'border-emerald-400/60 bg-emerald-400/[0.04]':'border-white/8 bg-white/[0.02]'}"><div class="text-sm text-white/60">${p[2]}</div><div class="text-3xl font-bold mt-1">${p[1]}<span class="text-sm text-white/50">/mo</span></div><div class="text-lg font-semibold mt-1">${p[0]}</div><button class="mt-4 w-full py-2 rounded-lg ${i===1?'bg-emerald-400 text-emerald-950':'bg-white/10 text-white'} font-bold text-sm">Choose plan</button></div>`).join('')}
    </section>
  </div>`;
  return shell(body, { bg: '#0A0A0F', color: '#fff' });
}

function coachPreview() {
  const body = `
  <div class="min-h-screen bg-[#0B0F14] text-white flex">
    <aside class="w-60 border-r border-white/5 p-6">
      <div class="flex items-center gap-2 mb-8"><div class="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-400"></div><span class="font-bold">Pulse</span></div>
      ${['Today','Plan','Progress','Chat','Settings'].map((t,i)=>`<div class="flex items-center gap-3 px-3 py-2 rounded-lg ${i===0?'bg-emerald-400/15 text-emerald-300':'text-white/65'} mb-1 text-sm"><div class="w-1.5 h-1.5 rounded-full ${i===0?'bg-emerald-400':'bg-white/30'}"></div>${t}</div>`).join('')}
    </aside>
    <main class="flex-1 p-10">
      <div class="flex items-center justify-between mb-8"><div><div class="text-xs text-white/45 uppercase tracking-wider">Tuesday · Week 3</div><h1 class="text-3xl font-bold mt-1">Upper body · Push</h1></div><div class="flex items-center gap-3"><div class="w-16 h-16 rounded-full border-4 border-emerald-400 flex items-center justify-center font-bold">68%</div></div></div>
      <div class="grid grid-cols-2 gap-4">
        ${['Bench Press · 4×8','Overhead Press · 3×10','Incline Dumbbell · 4×8','Tricep Dips · 3×12'].map((t,i)=>`<div class="p-5 rounded-2xl bg-white/[0.04] border border-white/8"><div class="text-xs text-emerald-300 uppercase tracking-wider mb-2">Exercise ${i+1}</div><div class="font-semibold">${t}</div><div class="mt-3 flex gap-2">${[1,2,3,4].map(s=>`<div class="flex-1 h-1.5 rounded-full ${s<=i+1?'bg-emerald-400':'bg-white/10'}"></div>`).join('')}</div></div>`).join('')}
      </div>
      <div class="mt-8 p-5 rounded-2xl bg-white/[0.03] border border-white/8">
        <div class="text-xs text-white/45 uppercase tracking-wider mb-3">Coach · live</div>
        <div class="space-y-2 text-sm"><div class="px-3 py-2 rounded-lg bg-emerald-400/10 inline-block text-white/85">Nice tempo on set 2 — try a 3s eccentric on the next one.</div></div>
      </div>
    </main>
  </div>`;
  return shell(body, { bg: '#0B0F14', color: '#fff' });
}

function portfolioPreview() {
  const body = `
  <div class="min-h-screen bg-[#F4F2EB] text-[#1A1A1A]">
    <nav class="flex items-center justify-between px-16 py-6"><span class="font-bold text-xl" style="font-family:'Playfair Display',serif">Anaïs Reine</span><div class="flex gap-8 text-sm">Work · About · Journal · Contact</div></nav>
    <section class="px-16 pt-16 pb-10"><div class="text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/60 mb-6">Product Designer · Paris</div><h1 class="text-7xl font-normal leading-[0.95] tracking-tight" style="font-family:'Playfair Display',serif">Designing<br/>quiet products<br/><em class="text-[#9B7B3A]">for loud lives.</em></h1></section>
    <section class="px-16 grid grid-cols-2 gap-6 pb-20">
      ${[['Forma · 2024','Brand system'],['Halo · 2024','Mobile app'],['Sable · 2023','E-commerce'],['Lune · 2023','Identity']].map((p,i)=>`<div class="aspect-[4/3] rounded-md bg-gradient-to-br ${['from-stone-300 to-stone-500','from-amber-200 to-amber-500','from-emerald-200 to-emerald-700','from-indigo-200 to-indigo-700'][i]} relative overflow-hidden"><div class="absolute bottom-4 left-4 text-white"><div class="font-semibold">${p[0]}</div><div class="text-xs opacity-80">${p[1]}</div></div></div>`).join('')}
    </section>
  </div>`;
  return shell(body, { bg: '#F4F2EB', color: '#1A1A1A' });
}

function shopPreview() {
  const body = `
  <div class="min-h-screen bg-[#F6F0E8] text-[#2A1C12]">
    <nav class="flex items-center justify-between px-12 py-6"><span class="font-bold text-2xl tracking-tight" style="font-family:Georgia,serif">Brûlé.</span><div class="flex gap-6 text-sm">Shop · Story · Subscribe · Cart (2)</div></nav>
    <section class="px-12 grid grid-cols-2 gap-12 items-center pb-16"><div><div class="text-xs uppercase tracking-[0.2em] mb-4 text-[#7A4A24]">New harvest · Ethiopia</div><h1 class="text-6xl font-normal leading-tight" style="font-family:Georgia,serif">Coffee, slow-roasted in <em>Marseille</em>.</h1><p class="mt-5 text-[#2A1C12]/70 max-w-md">Single origin beans, roasted in small batches and shipped within 48 hours of harvest.</p><button class="mt-7 px-6 py-3 bg-[#2A1C12] text-[#F6F0E8] text-sm font-semibold">Shop the harvest →</button></div><div class="aspect-square rounded-md bg-gradient-to-br from-[#3E2415] to-[#7A4A24] flex items-center justify-center text-[#F6F0E8] text-lg" style="font-family:Georgia,serif">☕</div></section>
    <section class="px-12 grid grid-cols-3 gap-6 pb-20">
      ${[['Bahir Dar','Floral · Citrus','€22'],['Yirgacheffe','Berry · Honey','€24'],['Sidamo','Cocoa · Spice','€20']].map(p=>`<div><div class="aspect-square bg-gradient-to-br from-[#C8A87C] to-[#7A4A24] rounded-md mb-3"></div><div class="font-semibold">${p[0]}</div><div class="text-xs text-[#7A4A24] mb-1">${p[1]}</div><div class="text-sm">${p[2]}</div></div>`).join('')}
    </section>
  </div>`;
  return shell(body, { bg: '#F6F0E8', color: '#2A1C12' });
}

function analyticsPreview() {
  const body = `
  <div class="min-h-screen bg-[#0A0F1C] text-white flex">
    <aside class="w-56 border-r border-white/5 p-5"><div class="font-bold mb-8 flex items-center gap-2"><div class="w-6 h-6 rounded bg-indigo-500"></div>Sigma</div>${['Overview','Revenue','Customers','Products','Channels','Reports'].map((t,i)=>`<div class="px-3 py-2 rounded-lg text-sm ${i===0?'bg-indigo-500/20 text-indigo-300':'text-white/55'} mb-1">${t}</div>`).join('')}</aside>
    <main class="flex-1 p-8">
      <div class="flex items-center justify-between mb-7"><h1 class="text-2xl font-bold">Overview</h1><div class="flex gap-2 text-xs text-white/60"><span class="px-3 py-1 rounded-full bg-white/5">Last 30 days</span><span class="px-3 py-1 rounded-full bg-indigo-500/20 text-indigo-300">Export</span></div></div>
      <div class="grid grid-cols-4 gap-4 mb-6">
        ${[['Revenue','$124,580','+18%'],['Orders','3,412','+12%'],['Conv. rate','3.4%','+0.4'],['AOV','$36','-1.2%']].map((k,i)=>`<div class="p-5 rounded-xl bg-white/[0.04] border border-white/8"><div class="text-xs text-white/45 uppercase tracking-wider">${k[0]}</div><div class="text-2xl font-bold mt-1">${k[1]}</div><div class="text-xs mt-1 ${k[2].startsWith('+')?'text-emerald-400':'text-rose-400'}">${k[2]}</div></div>`).join('')}
      </div>
      <div class="grid grid-cols-3 gap-4">
        <div class="col-span-2 p-5 rounded-xl bg-white/[0.04] border border-white/8 h-56"><div class="text-xs text-white/50 mb-2">Revenue over time</div><svg viewBox="0 0 400 150" class="w-full h-40"><polyline fill="none" stroke="#818CF8" stroke-width="2" points="0,110 40,90 80,100 120,60 160,70 200,40 240,55 280,30 320,45 360,20 400,35"/><polyline fill="rgba(129,140,248,0.15)" stroke="none" points="0,110 40,90 80,100 120,60 160,70 200,40 240,55 280,30 320,45 360,20 400,35 400,150 0,150"/></svg></div>
        <div class="p-5 rounded-xl bg-white/[0.04] border border-white/8 h-56"><div class="text-xs text-white/50 mb-3">Traffic sources</div>${[['Direct',42,'bg-indigo-400'],['Search',31,'bg-emerald-400'],['Social',18,'bg-amber-400'],['Email',9,'bg-rose-400']].map(s=>`<div class="mb-2"><div class="flex justify-between text-xs mb-1"><span>${s[0]}</span><span class="text-white/50">${s[1]}%</span></div><div class="h-1.5 rounded-full bg-white/5"><div class="h-1.5 rounded-full ${s[2]}" style="width:${s[1]}%"></div></div></div>`).join('')}</div>
      </div>
    </main>
  </div>`;
  return shell(body, { bg: '#0A0F1C', color: '#fff' });
}

function restaurantPreview() {
  const body = `
  <div class="min-h-screen bg-[#FAF4EA] text-[#231914]">
    <nav class="flex items-center justify-between px-14 py-6"><span class="text-2xl tracking-wider" style="font-family:Georgia,serif">Maison Lune</span><div class="flex gap-7 text-sm">Menu · Story · Réserver · Contact</div></nav>
    <section class="relative h-[520px] flex items-end px-14 pb-14" style="background:linear-gradient(135deg,#3B1F12 0%,#7A3F23 60%,#B4733A 100%)"><div class="text-[#FAF4EA]"><div class="text-xs uppercase tracking-[0.3em] mb-4">Paris · 11ᵉ</div><h1 class="text-7xl leading-[1] tracking-tight" style="font-family:Georgia,serif">Cuisine du <em>marché</em>,<br/>au coin de la rue.</h1><div class="mt-7 flex gap-3"><button class="px-5 py-3 bg-[#FAF4EA] text-[#231914] text-sm font-semibold">Réserver</button><button class="px-5 py-3 border border-[#FAF4EA]/40 text-sm">Voir le menu</button></div></div></section>
    <section class="px-14 py-16"><div class="text-xs uppercase tracking-[0.2em] text-[#7A3F23] mb-4">La Carte</div><div class="grid grid-cols-2 gap-x-12 gap-y-4">${[['Burrata, tomates anciennes','14'],['Tartare de bœuf, frites maison','22'],['Cabillaud, jus de coquillages','26'],['Pavlova, fruits rouges','9']].map(d=>`<div class="flex justify-between border-b border-[#231914]/10 py-3"><span style="font-family:Georgia,serif">${d[0]}</span><span class="text-[#7A3F23]">${d[1]} €</span></div>`).join('')}</div></section>
  </div>`;
  return shell(body, { bg: '#FAF4EA', color: '#231914' });
}

function kanbanPreview() {
  const body = `
  <div class="min-h-screen bg-[#F5F6FA] text-[#1A1F36]">
    <nav class="flex items-center justify-between px-8 py-4 bg-white border-b border-slate-200"><div class="flex items-center gap-3"><div class="w-7 h-7 rounded-lg bg-indigo-500"></div><span class="font-bold">Spring launch</span><span class="text-xs text-slate-400">/ Marketing</span></div><div class="flex items-center gap-2"><div class="flex -space-x-2">${[1,2,3,4].map(i=>`<div class="w-7 h-7 rounded-full border-2 border-white bg-gradient-to-br ${['from-amber-400 to-rose-500','from-emerald-400 to-cyan-500','from-indigo-400 to-purple-500','from-rose-400 to-pink-500'][i-1]}"></div>`).join('')}</div><button class="px-3 py-1.5 rounded-lg bg-indigo-500 text-white text-sm">+ Task</button></div></nav>
    <main class="p-6 grid grid-cols-4 gap-4">
      ${[['Backlog',['Audit landing page','Brief la photographe','Write H1 variants']],['Doing',['Design hero v2','Prep instagram launch']],['Review',['Email warm-up · QA']],['Done',['Pricing approved','Brand voice OK','Logo final']]].map((col,ci)=>`<div><div class="flex justify-between mb-3"><span class="text-xs font-semibold uppercase tracking-wider text-slate-500">${col[0]}</span><span class="text-xs text-slate-400">${col[1].length}</span></div><div class="space-y-2">${col[1].map((t,i)=>`<div class="p-3 rounded-lg bg-white border border-slate-200 shadow-sm"><div class="flex items-center gap-2 mb-2"><div class="w-1.5 h-1.5 rounded-full ${['bg-rose-500','bg-amber-500','bg-emerald-500'][i%3]}"></div><span class="text-[10px] uppercase tracking-wider text-slate-400">Marketing</span></div><div class="text-sm font-medium">${t}</div><div class="mt-3 flex items-center justify-between"><div class="flex -space-x-1.5">${[1,2].map(a=>`<div class="w-5 h-5 rounded-full border-2 border-white bg-gradient-to-br ${['from-indigo-400 to-purple-500','from-emerald-400 to-cyan-500'][a-1]}"></div>`).join('')}</div><span class="text-[10px] text-slate-400">Apr ${10+i}</span></div></div>`).join('')}</div></div>`).join('')}
    </main>
  </div>`;
  return shell(body, { bg: '#F5F6FA', color: '#1A1F36' });
}

function blogPreview() {
  const body = `
  <div class="min-h-screen bg-white text-black">
    <nav class="flex items-center justify-between px-14 py-6 border-b border-black/10"><span class="text-2xl font-bold tracking-tight">The Pulse.</span><div class="flex gap-7 text-sm">Culture · Tech · Style · Politics · Subscribe</div></nav>
    <section class="px-14 py-12 grid grid-cols-12 gap-10"><div class="col-span-7"><div class="aspect-[16/10] bg-gradient-to-br from-rose-500 via-amber-400 to-emerald-500 rounded-sm mb-6"></div><div class="text-xs uppercase tracking-[0.2em] text-rose-600 mb-2">Cover story</div><h1 class="text-5xl font-bold leading-tight tracking-tight">The slow web is winning. We just stopped paying attention.</h1><p class="mt-4 text-black/65 text-lg">A new generation of independent publishers is rejecting the algorithm — and finding a quieter, more loyal audience.</p><div class="mt-4 text-xs text-black/50">By Léa Mercier · Apr 24 · 9 min read</div></div><div class="col-span-5 space-y-6">${[['Tech','Why React stopped being fun','Lior Ben'],['Culture','The renaissance of the zine','M. Okonkwo'],['Style','Inside Margiela\'s archive','C. Dubois']].map(a=>`<div class="flex gap-4"><div class="w-28 h-20 bg-gradient-to-br from-slate-300 to-slate-600 rounded-sm flex-shrink-0"></div><div><div class="text-[10px] uppercase tracking-wider text-rose-600">${a[0]}</div><div class="font-bold leading-snug mt-1">${a[1]}</div><div class="text-xs text-black/50 mt-2">${a[2]}</div></div></div>`).join('')}</div></section>
  </div>`;
  return shell(body, { bg: '#fff', color: '#000' });
}
